import React, { useEffect, useRef, useState } from "react";
import theme from "../../data/theme";
import ClipToggle from "./ClipToggle";
import BoardScaleControl from "./BoardScale";
import ShareLink from "./ShareBar";
import { turnSeat } from "../../utils/boardFocus";
import { teamIsFull } from "../../utils/liveBoard";

/**
 * BoardMenu — one ⋯ beside the tabs, holding everything rare.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 WHAT THIS REPLACED, AND WHY IT IS BETTER RATHER THAN TIDIER.
 *
 * Ben: "What is the skip ahead thing? I don't want that. If we ever need to fix
 * the current nominator maybe we should have a little menu for that and
 * anything similar."
 *
 * The per-card control was `nominatorIndex + 1`. Correcting a drifted
 * prediction on a ten-team board could therefore take NINE CLICKS, at the exact
 * moment somebody is waiting to nominate. `setNominator` has always taken an
 * index, so a list of seats sets any of them in one — the menu is not a tidier
 * home for that control, it is the first version of it that works.
 *
 * "Anything similar" is the important half of the sentence. Every setup-time
 * control moved in with it, which reclaims a permanent row of board on all 168
 * picks and gives the TV work a single escape hatch instead of a hover strip.
 * ══════════════════════════════════════════════════════════════════════
 *
 * ⚠️ TWO WRITTEN RULES ARE BENT HERE, KNOWINGLY.
 *
 * 1. The clips toggle's own comment: "the way out, visible and one click,
 *    because Aug 22 is a real auction and 'looks bad on the TV' cannot mean
 *    'wait for a deploy'." A menu makes it two. Accepted, because a menu is
 *    immediate and always present where a permanent row costs board on every
 *    screen for the whole draft — and it is FIRST IN THE PANEL, never behind a
 *    submenu, so it is the thing under the cursor when the menu opens.
 *
 * 2. BoardScale.jsx: the in-product text-size control exists because "eleven
 *    people will not discover [Ctrl +]". That argument is about SPECTATORS, not
 *    the operator. So it is in the menu here and stays VISIBLE on /watch: Ben
 *    knows his own app; eleven people on phones do not.
 *
 * 🔴 THE TEXT SIZE IS ALSO THE WAY OUT OF TV MODE. It may move, it may not be
 * removed — without it the only way back from the largest preset is
 * localStorage or a hard refresh.
 */
/**
 * One string, used as the button's text AND its accessible name, so what a
 * screen reader announces and what the room sees cannot drift apart.
 *
 * ⚠️ IT IS NOT ALSO A HEADING INSIDE THE PANEL. Rule 1 in the header above puts
 * the clips toggle FIRST so it is under the cursor the moment the menu opens; a
 * title bar would take that spot to repeat the word you just clicked.
 */
const MENU_TITLE = "DRAFT OPTIONS & SHARING";

/**
 * The clock presets. "off" is a real choice and stays first.
 *
 * 🔴 45 CAME OFF AND 90 WENT ON, and the default moved to 60 (see the
 * normalizer in liveBoard.js). Three lengths plus a free field beats four
 * lengths: 45 and 60 are the same decision, and the leagues that wanted 90 had
 * no way to say so at all.
 */
const CLOCK_CHIPS = [0, 30, 60, 90];
const CUSTOM_MIN = 5;
const CUSTOM_MAX = 600;

export default function BoardMenu({
  board, syncOk, onSetNominator, onOpenTeamNames, onExportCsv, onStartOver, onSetNomClock,
  clipsOn, onClipsChange, scale, onScaleChange, onLeave,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Click-away and Escape. Bound only while open — a listener that lives for
  // the whole draft to do nothing is the kind of thing that ends up in a
  // profile at pick 120.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const label = {
    fontSize: theme.fontSize.micro, textTransform: "uppercase", letterSpacing: "0.08em",
    color: theme.colors.text.faint, fontWeight: theme.fontWeight.bold,
    marginBottom: 6, display: "block",
  };
  const item = {
    display: "block", width: "100%", textAlign: "left",
    background: "transparent", border: "none", borderRadius: theme.radius.sm,
    color: theme.colors.text.primary, fontFamily: "inherit",
    fontSize: theme.fontSize.body, padding: "7px 10px", cursor: "pointer",
  };
  const rule = { height: 1, background: theme.colors.border.subtle, margin: "12px 0" };

  const close = (fn) => () => { setOpen(false); if (fn) fn(); };

  /**
   * The nomination clock, in seconds. The chips cover the three lengths a
   * league actually argues about; the field covers everything else.
   *
   * 🔴 THE FIELD IS SEEDED FROM THE BOARD, NOT FROM A KEYSTROKE, and re-seeds
   * whenever the board's value changes — so clicking a chip clears a stale
   * custom entry rather than leaving two controls disagreeing about what the
   * clock is. Committed on Enter or blur, never per character: a half-typed
   * "7" on the way to "75" would otherwise publish a seven-second clock to
   * every spectator in the room.
   */
  const clockSec = board?.nomClockSec ?? 0;
  const customIsCurrent = clockSec > 0 && !CLOCK_CHIPS.includes(clockSec);
  const [customDraft, setCustomDraft] = useState(() => (customIsCurrent ? String(clockSec) : ""));
  useEffect(() => {
    const isCustom = clockSec > 0 && !CLOCK_CHIPS.includes(clockSec);
    setCustomDraft(isCustom ? String(clockSec) : "");
  }, [clockSec]);

  const applyCustom = () => {
    const n = parseInt(customDraft, 10);
    // Out of range or empty puts the field back to what the board says rather
    // than silently keeping a number nothing acted on.
    if (!Number.isFinite(n) || n < CUSTOM_MIN || n > CUSTOM_MAX) {
      setCustomDraft(customIsCurrent ? String(clockSec) : "");
      return;
    }
    if (n !== clockSec) onSetNomClock(n);
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      {/* 🔴 IT SAYS WHAT IS BEHIND IT, AND IT LOOKS LIKE A DESTINATION.
          "⋯ BOARD" was a glyph and a word that repeated the name of the page
          it was already on, in the muted style every inert control here uses —
          so the one button holding the watch link, the team names, the clock
          and the way out read as decoration. The share link in particular is
          the thing an operator needs in the first thirty seconds and could not
          find. Filled orange, the same treatment as the ACTIVE tab beside it:
          this is a place you go, not a state you are in. Same position. */}
      <button
        data-testid="board-menu"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={MENU_TITLE}
        style={{
          background: theme.colors.brand.orange,
          border: `1px solid ${theme.colors.brand.orange}`,
          borderRadius: theme.radius.md, color: "#fff",
          fontFamily: "inherit", fontSize: theme.fontSize.caption,
          fontWeight: theme.fontWeight.bold, letterSpacing: "0.04em",
          padding: "5px 14px", cursor: "pointer", whiteSpace: "nowrap",
        }}
      >{MENU_TITLE}</button>

      {open && (
        <div
          data-testid="board-menu-panel"
          role="menu"
          style={{
            position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 130,
            width: 268, maxHeight: "70vh", overflowY: "auto",
            background: theme.colors.extended.modalBg,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: theme.radius.lg, padding: 14,
            boxShadow: "0 18px 44px rgba(0,0,0,0.55)",
          }}
        >
          {/* 🔴 FIRST, DELIBERATELY. See rule 1 in the header — this is the one
              control here whose whole reason for existing is how fast it can
              be reached in a room. It does not close the menu: killing the
              backdrop is something you judge by looking at the screen behind
              this panel, and reopening the menu to undo it is worse. */}
          <span style={label}>Highlight clips</span>
          <ClipToggle on={clipsOn} onChange={onClipsChange} />

          <div style={rule} />

          {/* 🔴 ADVISORY, AND NOW CORRECTABLE IN ONE CLICK TO ANY SEAT. Real
              auctions skip full teams and drift; nothing gates on this value. */}
          <span style={label}>Whose turn is it?</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {(board?.teamNames || []).map((name, i) => {
              // 🔴 THE SAME FUNCTION THE BATON'S BRIGHT MARK READS. This was
              // `i === board.nominatorIndex`, which agreed with the glow by
              // coincidence rather than by construction — one guard, one answer.
              const on = i === turnSeat(board);
              /**
               * 🔴 A WARNING, NOT A BLOCK. A team with no roster space left
               * cannot nominate, and naming it is a prediction wrong on its
               * face — so the row says so. It stays CLICKABLE and checkable,
               * because this list IS the escape hatch: real auctions drift in
               * ways no rule anticipates, and `setNominator` is the one control
               * that can name any seat. The step arrows in the entry bar are
               * the filtered tool; this one is deliberately not.
               */
              const done = board ? teamIsFull(board, i) : false;
              return (
                <button
                  key={i}
                  role="menuitemradio"
                  aria-checked={on}
                  onClick={close(() => onSetNominator(i))}
                  style={{
                    ...item,
                    background: on ? "rgba(255,149,0,0.14)" : "transparent",
                    color: on ? theme.colors.brand.accent : theme.colors.text.primary,
                    fontWeight: on ? theme.fontWeight.bold : 400,
                  }}
                >
                  {name}
                  {done && (
                    <span
                      data-testid={`menu-team-full-${i}`}
                      style={{
                        marginLeft: 8, fontSize: theme.fontSize.micro,
                        color: theme.colors.text.faint, fontWeight: 400,
                        letterSpacing: "0.04em",
                      }}
                    >roster full</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* ══════════════════════════════════════════════════════════
              🔴 THE THREE RECOVERIES, WRITTEN DOWN WHERE THE PANIC HAPPENS.
              Every one of these already worked; none of them was discoverable
              at the moment it was needed. A price typed wrong in front of ten
              people, or a manager who left at pick 90, is not a moment anybody
              spends exploring a UI — and the operator who does not know Undo
              exists enters the wrong number and lives with it.

              Under the team list on purpose: the seat list is what somebody
              opens this menu for when the room has drifted, so it is the thing
              they are already looking at when they need the other two.
              ══════════════════════════════════════════════════════════ */}
          <div style={rule} />

          <span style={label}>During the draft</span>
          <div data-testid="menu-during-draft" style={{
            fontSize: theme.fontSize.caption, color: theme.colors.text.muted, lineHeight: 1.55,
          }}>
            Undo takes back the last sale. Skip a nominator who left with the arrows next
            to TURN. Edit any sale from DRAFT LOG.
          </div>

          <div style={rule} />

          <span style={label}>Nomination clock</span>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            {CLOCK_CHIPS.map(n => (
              <button
                key={n}
                onClick={() => onSetNomClock(n)}
                aria-pressed={clockSec === n}
                style={{
                  background: clockSec === n ? theme.colors.brand.orange : "transparent",
                  border: `1px solid ${clockSec === n ? theme.colors.brand.orange : theme.colors.border.default}`,
                  borderRadius: theme.radius.sm,
                  color: clockSec === n ? "#fff" : theme.colors.text.muted,
                  fontFamily: "inherit", fontSize: theme.fontSize.micro,
                  padding: "3px 10px", cursor: "pointer",
                }}
              >{n === 0 ? "off" : `${n}s`}</button>
            ))}
            {/* 🔴 THE FIELD IS ALSO THE READOUT. A league running 75s has no
                chip to light up, so without this the panel would show four
                unpressed chips and no sign of what the clock actually is. It
                carries the current value whenever that value is not a chip. */}
            <input
              data-testid="nom-clock-custom"
              aria-label="Custom seconds"
              placeholder="custom"
              inputMode="numeric"
              value={customDraft}
              onChange={e => setCustomDraft(e.target.value.replace(/[^0-9]/g, ""))}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); applyCustom(); } }}
              onBlur={applyCustom}
              style={{
                background: theme.colors.bg.raised,
                border: `1px solid ${customIsCurrent ? theme.colors.brand.orange : theme.colors.border.default}`,
                borderRadius: theme.radius.sm,
                color: customIsCurrent ? theme.colors.brand.accent : theme.colors.text.primary,
                fontFamily: "inherit", fontSize: theme.fontSize.micro,
                padding: "3px 6px", width: 62, outline: "none",
              }}
            />
            <span style={{ fontSize: theme.fontSize.nano, color: theme.colors.text.faint }}>sec</span>
          </div>

          <div style={rule} />

          {/* Stays a full control rather than becoming a list of links: it is
              also the way OUT of TV mode, and it must not close the menu —
              you pick a size by looking at the board while you change it. */}
          <BoardScaleControl scale={scale} onChange={onScaleChange} />

          <div style={rule} />

          {/* 🔴 THE LINK, NOT THE ALARM. Copying it is a setup act — once,
              before anyone sits down — so it belongs behind a button. The
              FAILED-sync state is a different kind of thing and stays on screen
              at every scale, including TV; see SyncAlarm in ShareBar.jsx. Does
              not close the menu: you copy the link and then send it. */}
          <span style={label}>Watch link</span>
          <ShareLink shareId={board?.shareId} syncOk={syncOk} />

          <div style={rule} />

          <button role="menuitem" style={item} onClick={close(onOpenTeamNames)}>Team names…</button>
          <button
            role="menuitem"
            style={{ ...item, color: board?.sales?.length ? theme.colors.text.primary : theme.colors.text.faint,
              cursor: board?.sales?.length ? "pointer" : "default" }}
            disabled={!board?.sales?.length}
            onClick={close(onExportCsv)}
          >Export CSV</button>
          {/* 🔴 THE ESCAPE HATCH. Chunk 5 hides the app header at TV, and Ben
              mirrors his laptop — so anything hidden "for the TV" is hidden
              from the person running the board. This is how he gets out.
              Absent when nobody gave us a way out, rather than a dead item. */}
          {onLeave && (
            <button role="menuitem" style={item} onClick={close(onLeave)}>Leave the live board</button>
          )}

          <div style={rule} />

          {/* Last, and the only red thing here. It erases the auction. */}
          <button
            role="menuitem"
            style={{ ...item, color: "#fca5a5" }}
            onClick={close(onStartOver)}
          >Start over</button>
        </div>
      )}
    </div>
  );
}
